import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Overlay from '@clearscore/rainbow.overlay';
import Text from '@clearscore/rainbow.text';
import Spacer from '@clearscore/rainbow.spacer';
import useLanguage from '@clearscore/hooks.use-language';
import Button from '@clearscore/rainbow.button';
import ButtonGroup from '@clearscore/rainbow.button-group';
import useMarketConfig from '@clearscore/hooks.use-market-config';

import styles from './exclusive-modal.css';
import lang from './lib/lang';
import marketConfig from './lib/market-config';
import ExclusiveCard from './assets/exclusive-card.png';
import useVerticalTracking, { constants } from './lib/hooks/use-tracking';

const ExclusiveModal = ({ isModalShown, handleModalVisibility }) => {
    const { header, body, compliance, loansBtn, cardsBtn, exclusiveImageAlt } = useLanguage(lang);
    const { cardsHref, loansHref } = useMarketConfig(marketConfig);
    const { trackModalOpen, trackCtaClick } = useVerticalTracking();

    useEffect(() => {
        if (isModalShown) trackModalOpen();
    }, [isModalShown]);

    return (
        <Overlay
            onClose={() => {
                trackCtaClick({ ctaType: constants.BUTTON_TRACKING_TYPES.DISMISS });
                handleModalVisibility();
            }}
            hasBackdrop
            isWrappedContent
            isShown={isModalShown}
            onBackdropClick={() => {
                trackCtaClick({ ctaType: constants.BUTTON_TRACKING_TYPES.DISMISS });
                handleModalVisibility();
            }}
            dataId="exclusive-modal"
        >
            <div className={styles.container}>
                <Text.H2 dataId="exclusive-modal-header">{header}</Text.H2>
                <Spacer all={{ vertical: Spacer.spacings.MEDIUM }}>
                    <Text.Body2 dataId="exclusive-modal-body">{body}</Text.Body2>
                </Spacer>
                <img
                    data-id="exclusive-modal-image"
                    src={ExclusiveCard}
                    className={styles.complianceImage}
                    alt={exclusiveImageAlt}
                />

                <Spacer all={{ bottom: Spacer.spacings.BIG }}>
                    <div className={styles.complianceCopy}>
                        <Text.Body2 dataId="exclusive-modal-compliance">{compliance}</Text.Body2>
                    </div>
                </Spacer>

                <ButtonGroup isResponsive>
                    <Button
                        dataId="exclusive-modal-credit-cards-btn"
                        href={cardsHref}
                        isFlat
                        isResponsive
                        size={Button.sizes.LARGE}
                        theme={Button.themes.DARK}
                        type={Button.types.PRIMARY}
                        onClick={() =>
                            trackCtaClick({
                                ctaType: constants.BUTTON_TRACKING_TYPES.CTA,
                                destination: cardsHref,
                                ctaCopy: cardsBtn,
                            })
                        }
                    >
                        {cardsBtn}
                    </Button>
                    <Button
                        dataId="exclusive-modal-loans-btn"
                        href={loansHref}
                        isFlat
                        isResponsive
                        size={Button.sizes.LARGE}
                        theme={Button.themes.DARK}
                        type={Button.types.PRIMARY}
                        onClick={() =>
                            trackCtaClick({
                                ctaType: constants.BUTTON_TRACKING_TYPES.CTA,
                                destination: loansHref,
                                ctaCopy: loansBtn,
                            })
                        }
                    >
                        {loansBtn}
                    </Button>
                </ButtonGroup>
            </div>
        </Overlay>
    );
};

ExclusiveModal.propTypes = {
    isModalShown: PropTypes.bool.isRequired,
    handleModalVisibility: PropTypes.func.isRequired,
};

export default ExclusiveModal;
